import * as React from 'react';

import {
  ItemSortOption,
  enumItemSortOption,
} from '@rematter/frontend-lib-api-client';
import { ItemRecord } from '@rematter/modules/settings/types/inventoryItem';
import {
  Button,
  DataGrid,
  ListScreenLayout,
  Pagination,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import * as state from '../../../_state/itemsList.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { useItemsColumns } from './hooks/useItemsColumns';
import { ItemCreateDialog } from './ItemCreateDialog';
import { ItemListFilterBar } from './ItemListFilterBar';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: ItemSortOption; desc: ItemSortOption }
> = {
  name: {
    asc: enumItemSortOption.NameAsc,
    desc: enumItemSortOption.NameDesc,
  },
  skuNumber: {
    asc: enumItemSortOption.SkuAsc,
    desc: enumItemSortOption.SkuDesc,
  },
  createdAt: {
    asc: enumItemSortOption.CreatedAtAsc,
    desc: enumItemSortOption.CreatedAtDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ ITEMS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const ItemsListView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  // Atoms
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteItems,
  );
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCreateDialogOpen,
  );
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  // Callbacks
  const handleRowClick = React.useCallback(
    (item: ItemRecord) => {
      setLocation(`/inventory/items/${item.id}`);
    },
    [setLocation],
  );
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumItemSortOption.NameAsc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );
  const handleCreateClick = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);

  // Other data hooks
  const { columns, columnVisibilityState } = useItemsColumns({
    onRowClick: handleRowClick,
  });

  const items = data?.items ?? [];
  const total = data?.totalCount ?? 0;
  const emptyPanelProps = {
    title: t.inventoryView.itemsPanel.emptyState.title,
    description: (
      <span>{t.inventoryView.itemsPanel.emptyState.description}</span>
    ),
    action: (
      <Button
        onPress={handleCreateClick}
        variant="solid"
        color="primary"
        icon={<LucidePlusCircle />}
      >
        {t.inventoryView.itemsPanel.actions.create}
      </Button>
    ),
    centered: true,
  };

  return (
    <ListScreenLayout
      filters={<ItemListFilterBar />}
      actions={
        <Button
          variant="solid"
          color="primary"
          icon={<LucidePlusCircle />}
          onPress={handleCreateClick}
        >
          {t.inventoryView.itemsPanel.actions.create}
        </Button>
      }
      footer={
        total > limit ? (
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        ) : undefined
      }
      className="@ItemsListView"
    >
      <DataGrid<ItemRecord>
        isLoading={loadingState.isInitialLoading || isFetching}
        columns={columns}
        columnVisibilityState={columnVisibilityState}
        dataSource={items}
        rowKey="id"
        aria-label={t.inventoryView.itemsPanel.ariaLabel}
        selectionMode="multiple"
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        onColumnSort={handleColumnSort}
        className="w-full text-sm"
        fullWidth
        hasError={!!error}
        errorPanelProps={
          error
            ? {
                description: t.inventoryView.itemsPanel.errors.loadingFailed(
                  error.message,
                ),
                centered: true,
              }
            : undefined
        }
        emptyPanelProps={emptyPanelProps}
      />
      <ItemCreateDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={() => refetch?.()}
      />
    </ListScreenLayout>
  );
};
